import React, { useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink, Copy, Check } from 'lucide-react';

interface AgentResponseRendererProps {
  responseText?: string;
  responseJson?: any;
}

/**
 * Renders agent responses with:
 * - Markdown formatting (bold, newlines)
 * - Dynamic JSON parsing and display
 * - Collapsible sections for each field
 * - URL detection and linking
 * - Copy-to-clipboard functionality
 */
export function AgentResponseRenderer({ responseText, responseJson }: AgentResponseRendererProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['answer']));
  const [copiedField, setCopiedField] = useState<string | null>(null);

  console.log('[AgentResponseRenderer] Props:', {
    responseText,
    responseText_type: typeof responseText,
    responseText_length: responseText?.length,
    responseJson,
    responseJson_type: typeof responseJson
  });

  // If we have JSON, prefer that over text
  // If text isn't valid JSON, use the text as-is (fallback to raw text)
  const data = responseJson || (responseText ? (tryParseJSON(responseText) || responseText) : null);

  console.log('[AgentResponseRenderer] Processed data:', {
    data,
    data_type: typeof data,
    data_length: typeof data === 'string' ? data.length : 'N/A'
  });

  const toggleSection = (key: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleCopy = async (text: string, fieldKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // If data is a simple string, render with markdown
  if (typeof data === 'string') {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="prose prose-sm max-w-none">
          <MarkdownText text={data} />
        </div>
      </div>
    );
  }

  // If data is null or undefined, show fallback
  if (!data) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <p className="text-sm text-gray-500 italic">No response data available</p>
      </div>
    );
  }

  // Render JSON object with collapsible sections
  return (
    <div className="space-y-3">
      {Object.entries(data).map(([key, value]) => {
        const isExpanded = expandedSections.has(key);
        const displayKey = formatFieldName(key);

        return (
          <div key={key} className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
            {/* Section Header */}
            <button
              onClick={() => toggleSection(key)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                )}
                <span className="font-medium text-gray-900">{displayKey}</span>
                <span className="text-xs text-gray-500">
                  {getValueTypeLabel(value)}
                </span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const textValue = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
                  handleCopy(textValue, key);
                }}
                className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                title="Copy to clipboard"
              >
                {copiedField === key ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </button>

            {/* Section Content */}
            {isExpanded && (
              <div className="px-4 pb-4 border-t border-gray-200 bg-white">
                <div className="mt-3">
                  <ValueRenderer value={value} />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Renders different types of values appropriately
 */
function ValueRenderer({ value }: { value: any }) {
  // String with markdown
  if (typeof value === 'string') {
    return (
      <div className="prose prose-sm max-w-none">
        <MarkdownText text={value} />
      </div>
    );
  }

  // Array
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <p className="text-sm text-gray-500 italic">Empty array</p>;
    }

    // Array of strings (like URLs)
    if (value.every(v => typeof v === 'string')) {
      return (
        <ul className="space-y-2">
          {value.map((item, idx) => (
            <li key={idx} className="text-sm">
              {isURL(item) ? (
                <a
                  href={item}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:underline"
                >
                  <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="break-all">{item}</span>
                </a>
              ) : (
                <span className="text-gray-900">{item}</span>
              )}
            </li>
          ))}
        </ul>
      );
    }

    // Array of objects
    return (
      <div className="space-y-3">
        {value.map((item, idx) => (
          <div key={idx} className="bg-gray-50 rounded p-3 border border-gray-200">
            <div className="text-xs font-medium text-gray-500 mb-2">Item {idx + 1}</div>
            <ValueRenderer value={item} />
          </div>
        ))}
      </div>
    );
  }

  // Object
  if (typeof value === 'object' && value !== null) {
    return (
      <div className="space-y-2">
        {Object.entries(value).map(([k, v]) => (
          <div key={k} className="flex gap-3">
            <span className="text-sm font-medium text-gray-600 min-w-[120px]">
              {formatFieldName(k)}:
            </span>
            <div className="flex-1">
              <ValueRenderer value={v} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Boolean
  if (typeof value === 'boolean') {
    return (
      <span className={`text-sm font-medium ${value ? 'text-green-600' : 'text-red-600'}`}>
        {value ? 'true' : 'false'}
      </span>
    );
  }

  // Number
  if (typeof value === 'number') {
    return <span className="text-sm font-mono text-gray-900">{value.toLocaleString()}</span>;
  }

  // Null/undefined
  if (value === null || value === undefined) {
    return <span className="text-sm text-gray-500 italic">null</span>;
  }

  // Fallback - stringify
  return <span className="text-sm text-gray-900 font-mono">{String(value)}</span>;
}

/**
 * Renders text with markdown-like formatting:
 * - **bold** → <strong>
 * - \n → line breaks
 * - URLs → clickable links
 */
function MarkdownText({ text }: { text: string }) {
  // Split by newlines to handle paragraphs
  const paragraphs = text.split('\n').filter(p => p.trim());

  return (
    <div className="space-y-3">
      {paragraphs.map((paragraph, pIdx) => {
        // Check if this is a list item (starts with *, -, or bullet point)
        const isListItem = /^[\*\-\•]\s+/.test(paragraph);

        if (isListItem) {
          return (
            <li key={pIdx} className="ml-4 text-sm text-gray-900">
              {renderFormattedText(paragraph.replace(/^[\*\-\•]\s+/, ''))}
            </li>
          );
        }

        // Regular paragraph
        return (
          <p key={pIdx} className="text-sm text-gray-900 leading-relaxed">
            {renderFormattedText(paragraph)}
          </p>
        );
      })}
    </div>
  );
}

/**
 * Renders text with inline formatting (bold, links)
 */
function renderFormattedText(text: string) {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  // Match **bold** patterns
  const boldRegex = /\*\*(.+?)\*\*/g;
  let match;

  while ((match = boldRegex.exec(text)) !== null) {
    // Add text before the bold
    if (match.index > lastIndex) {
      parts.push(linkifyText(text.substring(lastIndex, match.index)));
    }

    // Add bold text
    parts.push(
      <strong key={match.index} className="font-semibold text-gray-900">
        {match[1]}
      </strong>
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(linkifyText(text.substring(lastIndex)));
  }

  return parts.length > 0 ? parts : linkifyText(text);
}

/**
 * Convert URLs in text to clickable links
 */
function linkifyText(text: string): React.ReactNode {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, idx) => {
    if (isURL(part)) {
      return (
        <a
          key={idx}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1"
        >
          {part}
          <ExternalLink className="w-3 h-3 inline" />
        </a>
      );
    }
    return part;
  });
}

/**
 * Format field names for display
 */
function formatFieldName(key: string): string {
  // Convert snake_case and camelCase to Title Case
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get a label describing the value type
 */
function getValueTypeLabel(value: any): string {
  if (typeof value === 'string') {
    if (isURL(value)) return 'URL';
    return `text (${value.length} chars)`;
  }
  if (Array.isArray(value)) {
    return `array (${value.length} items)`;
  }
  if (typeof value === 'object' && value !== null) {
    return `object (${Object.keys(value).length} fields)`;
  }
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  return typeof value;
}

/**
 * Check if a string is a valid URL
 */
function isURL(str: string): boolean {
  if (typeof str !== 'string') return false;
  return /^https?:\/\//i.test(str);
}

/**
 * Try to parse JSON from string, return null on failure
 */
function tryParseJSON(text: string): any {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
